import { useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';

const NeonCursor = () => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      z-index: 99999;
      pointer-events: none;
      mix-blend-mode: screen;
    `;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;

    let vx = 0, vy = 0;
    let targetX = x, targetY = y;

    let lastMoveTime = Date.now();
    let idlePersona = 0;
    let nextPersonaTime = Date.now() + 4000;
    let personaData = {};

    const particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    resize();

    const onMove = (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      lastMoveTime = Date.now();
    };

    const onTouch = (e) => {
      if (e.touches.length > 0) {
        targetX = e.touches[0].clientX;
        targetY = e.touches[0].clientY;
        lastMoveTime = Date.now();
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onTouch);
    document.addEventListener('touchstart', onTouch);

    function moveTo(tx, ty, stiffness = 0.1, damping = 0.8) {
      vx += (tx - x) * stiffness;
      vy += (ty - y) * stiffness;

      vx *= damping;
      vy *= damping;

      x += vx;
      y += vy;

      const m = 20;
      x = Math.max(m, Math.min(canvas.width - m, x));
      y = Math.max(m, Math.min(canvas.height - m, y));
    }

    class Particle {
      constructor() {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 2;
        this.vy = (Math.random() - 0.5) * 2;
        this.life = 1;
        this.size = Math.random() * 4 + 1;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.95;
        this.vy *= 0.95;
        this.life -= 0.02;
      }
    }

    let frame;
    let lastTime = 0;

    const animate = (time = 0) => {
      if (time - lastTime < 16) {
        frame = requestAnimationFrame(animate);
        return;
      }
      lastTime = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const idleTime = Date.now() - lastMoveTime;
      const isIdle = idleTime > 5000;

      const t = time * 0.001;

      if (!isIdle) {
        moveTo(targetX, targetY, 0.2, 0.7);
      } else {
        if (Date.now() > nextPersonaTime) {
          idlePersona = (idlePersona + 1) % 4;
          nextPersonaTime = Date.now() + 4000 + Math.random() * 3000;
          personaData = {};
        }

        let tx = canvas.width / 2;
        let ty = canvas.height / 2;

        switch (idlePersona) {
          case 0:
            tx = canvas.width * (0.5 + Math.sin(t * 0.6) * 0.45);
            ty = canvas.height * (0.5 + Math.cos(t * 0.8) * 0.45);
            break;
          case 1:
            tx = canvas.width * (Math.sin(t * 2) * 0.5 + 0.5);
            ty = canvas.height * (Math.cos(t * 2.2) * 0.5 + 0.5);
            break;
          case 2:
            if (!personaData.p || Math.random() < 0.02) {
              personaData.p = {
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height
              };
            }
            tx = personaData.p.x;
            ty = personaData.p.y;
            break;
          case 3:
            tx = canvas.width * (0.5 + Math.cos(t * 0.9) * 0.48);
            ty = canvas.height * (0.5 + Math.sin(t * 0.9) * 0.48);
            break;
          default:
            break;
        }

        moveTo(tx, ty, 0.06, 0.85);
      }

      ctx.globalCompositeOperation = isIdle ? 'lighter' : 'source-over';

      if (!isIdle) {
        ctx.beginPath();
        ctx.fillStyle = isDark ? '#8884fd' : '#ff0000';
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const radius = 20 + Math.sin(t * 3) * 5;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);

        if (isDark) {
          grad.addColorStop(0, '#9900ff');
          grad.addColorStop(0.3, '#4837b2');
          grad.addColorStop(0.7, '#428bbbcb');
        } else {
          grad.addColorStop(0, '#00ff225d');
          grad.addColorStop(0.3, '#f42d00');
          grad.addColorStop(0.7, '#6e00ec');
        }

        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();

        if (particles.length < 40) {
          particles.push(new Particle());
        }

        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.update();

          ctx.globalAlpha = p.life;

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = isDark ? '#5cf7ffdc' : '#ff00d9';
          ctx.fill();

          if (p.life <= 0) particles.splice(i, 1);
        }

        ctx.globalAlpha = 1;
      }

      frame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('touchmove', onTouch);
      document.removeEventListener('touchstart', onTouch);
      window.removeEventListener('resize', resize);
      if (canvas.parentNode) document.body.removeChild(canvas);
    };
  }, [isDark]);

  return null;
};

export default NeonCursor;